import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * QA and consultant reviews may carry marked-up documents, so the body has to be
 * multipart when files are present. With no files it stays a plain JSON object, which
 * keeps the request identical to before for every existing caller.
 */
const toReviewBody = (val = {}) => {
	const { attachments, ...rest } = val;
	const files = (attachments || []).filter((f) => f instanceof File);

	if (!files.length) return rest;

	const formData = new FormData();
	Object.entries(rest).forEach(([key, value]) => {
		if (value !== null && value !== undefined) formData.append(key, value);
	});
	files.forEach((file) => formData.append("attachments", file));
	return formData;
};

export const SubmittalsAPI = createApi({
	reducerPath: "Submittal",
	// Needed so the MaintBackend socket can invalidate the cache on submittal events,
	// the same way the IR API does for backendTest events.
	tagTypes: ["Submittal", "SubmittalList"],
	baseQuery: fetchBaseQuery({
		baseUrl: process.env.REACT_APP_BACKEND2_URL,
		prepareHeaders: (headers) => {
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}
			return headers;
		},
	}),
	endpoints: (builder) => ({
		getAllSubmittals: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const sortOrder = val.sortorder ? `&sortOrder=${val.sortorder}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const status = val.status ? `&status=${val.status}` : "";
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `/v1/submittals?page=${page}&limit=${limit}${search}${sortBy}${sortOrder}${queryString}${status}`;
			},
			providesTags: [{ type: "SubmittalList", id: "LIST" }],
		}),
		getSubmittalsEnums: builder.query({
			query: () => `v1/submittals/enums/all`,
		}),
		getSingleSubmittals: builder.query({
			query: (id) => `/v1/submittals/${id}`,
			providesTags: (result, error, id) => [{ type: "Submittal", id }],
		}),
		addSubmittal: builder.mutation({
			query: (val) => {
				const formData = new FormData();

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						if (Array.isArray(value)) {
							if (value.length > 0) {
								if (value[0] instanceof File) {
									value.forEach((file) => formData.append(`${fullKey}`, file));
								} else {
									formData.append(fullKey, JSON.stringify(value));
								}
							} else {
								if (parentKey === "" && key === "attachments") {
									// Append nothing to indicate empty attachments[] array
									formData.append("attachments", "");
								} else {
									formData.append(fullKey, JSON.stringify([]));
								}
							}
						} else if (typeof value === "object" && !(value instanceof File)) {
							// Recurse for nested objects
							appendFormData(value, fullKey);
						} else {
							// Primitive value
							formData.append(fullKey, value);
						}
					}
				};

				appendFormData(val);

				return {
					url: `/v1/submittals`,
					method: "POST",
					body: formData,
				};
			},
		}),

		editSubmittal: builder.mutation({
			query: ({ val, id }) => {
				const formData = new FormData();

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];
						console.log("value", value);
						console.log("fullkey", fullKey);
						if (Array.isArray(value)) {
							if (key === "attachments") {
								// Only append if there are files
								if (value.length > 0) {
									value.forEach((file) => {
										if (file instanceof File) {
											formData.append("attachments", file); // Binary
										}
									});
								}
								// If no files, don't send attachments field at all
							} else {
								formData.append(fullKey, JSON.stringify(value));
							}
						} else if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
						} else {
							formData.append(fullKey, value);
						}
					}
				};

				appendFormData(val);
				return {
					url: `/v1/submittals/${id}`,
					method: "PATCH",
					body: formData,
				};
			},
		}),
		qaSubmittal: builder.mutation({
			query: ({ id, val }) => {
				return {
					url: `v1/submittals/${id}/qa-review`,
					method: "POST",
					body: toReviewBody(val),
				};
			},
		}),
		consultantQASubmittal: builder.mutation({
			query: ({ id, val }) => {
				return {
					url: `v1/submittals/${id}/consultant-review`,
					method: "POST",
					body: toReviewBody(val),
				};
			},
		}),
		resubmitSubmittal: builder.mutation({
			query: ({ id, val }) => {
				return {
					url: `v1/submittals/${id}/submit`,
					method: "POST",
					body: val,
				};
			},
		}),
	}),
});

export const {
	useGetAllSubmittalsQuery,
	useGetSubmittalsEnumsQuery,
	useGetSingleSubmittalsQuery,
	useAddSubmittalMutation,
	useEditSubmittalMutation,
	useQaSubmittalMutation,
	useConsultantQASubmittalMutation,
	useResubmitSubmittalMutation,
} = SubmittalsAPI;
